if (!customElements.get("predictive-search")) {
  customElements.define(
    "predictive-search",
    class PredictiveSearch extends SearchForm {
      constructor() {
        super();
        this.cachedResults = {};
        this.predictiveSearchEl = this.querySelector(
          "[data-predictive-search]"
        );
        this.allPredictInstances =
          document.querySelectorAll("predictive-search");
        this.isOpen = false;
        this.abortController = new AbortController();
        this.searchTerm = "";
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

        this.setupEventListeners();
      }

      setupEventListeners() {
        this.input.form.addEventListener(
          "submit",
          this.onFormSubmit.bind(this)
        );

        this.input.addEventListener("focus", this.onFocus.bind(this));
        this.addEventListener("keyup", this.onKeyup.bind(this));
        //this.addEventListener("focusout", this.onFocusOut.bind(this));
      }

      connectedCallback() {
        super.connectedCallback();
        document.addEventListener("pointerdown", this.onDocumentPointerDown);
      }

      disconnectedCallback() {
        document.removeEventListener("pointerdown", this.onDocumentPointerDown);
      }

      // A press anywhere off the field dismisses the results, same as the reset
      // cross — the typed term is kept, so re-focusing brings them back. closeResults
      // rather than close: re-trapping focus inside the header modal would put the
      // caret back in the input and reopen the panel we just dismissed.
      onDocumentPointerDown(event) {
        if (!this.hasAttribute("open") && !this.hasAttribute("loading")) return;
        if (this.contains(event.target)) return;

        this.closeResults();
        this.isOpen = false;
      }

      getQuery() {
        return this.input.value.trim();
      }

      onChange() {
        const newSearchTerm = this.getQuery();
        // The results panel only shows the cross while it is open; keep the inline
        // state truthful so a closed panel with a typed term still offers it.
        this.toggleResetBtn(newSearchTerm);

        if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
          // Remove the results when they are no longer relevant for the new search term
          // so they don't show up when the dropdown opens again
          this.querySelector("#predictive-search-results")?.remove();
        }

        // Update the term asap, don't wait for the predictive search query to finish loading
        this.updateSearchForTerm(this.searchTerm, newSearchTerm);

        this.searchTerm = newSearchTerm;

        if (!this.searchTerm.length) {
          this.close(true);
          if (this.predictiveSearchEl.closest(".search-section")) {
            this.getSearchResults(this.searchTerm);
          }
          return;
        }

        this.getSearchResults(this.searchTerm);
      }

      onFormSubmit(event) {
        if (
          !this.getQuery().length ||
          this.querySelector('[aria-selected="true"] .predictive-search__link')
        ) {
          event.preventDefault();
        }
      }

      onFormReset(event) {
        event.preventDefault();
        // if on search page and predictive are closed, clean the URL and current search term
        if (
          this.closest(".search-section") &&
          !this.searchTerm &&
          !this.hasAttribute("open") &&
          window.location.search
        ) {
          const cleanURL = window.location.pathname;
          window.location.href = cleanURL;
          return;
        }

        this.searchTerm = "";
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);

        // if on search page and predictive are open, return value of query parameter to input
        if (this.closest(".search-section") && window.location.search) {
          const params = new URLSearchParams(window.location.search);
          const q = params.get("q");
          if (q) {
            this.input.value = q;
            this.resetBtn.style.display = "flex";
          } else {
            const cleanURL = window.location.pathname;
            window.location.href = cleanURL;
          }
        } else {
          this.input.focus();
        }
      }

      onFocus() {
        const currentSearchTerm = this.getQuery();
        if (!currentSearchTerm.length) return;

        if (this.searchTerm !== currentSearchTerm) {
          // Search term was changed from other search input, treat it as a user change
          this.onChange();
        } else if (this.getAttribute("results") === "true") {
          this.open();
        } else {
          this.getSearchResults(this.searchTerm);
        }
      }

      //onFocusOut() {
      //  if (!this.closest(".header-search-modal")) {
      //    setTimeout(() => {
      //      if (!this.contains(document.activeElement)) this.close(true);
      //    });
      //  }
      //}

      onKeyup(event) {
        // Only typing in the field counts: the keyup of the Tab that moved focus on
        // to the submit button lands here too, and closing on it pulled focus back.
        if (event.target !== this.input) return;
        if (!this.getQuery().length) this.close(true);
        event.preventDefault();
      }

      updateSearchForTerm(previousTerm, newTerm) {
        const searchForTextElement = this.querySelector(
          "[data-predictive-search-search-for-text]"
        );
        const currentText = searchForTextElement?.innerText;
        if (currentText) {
          if (currentText.matchAll(new RegExp(previousTerm, "g")).length > 1) {
            // The new term matches part of the text and not just the search term, do not replace to avoid mistakes
            return;
          }
          const newText = currentText.replace(previousTerm, newTerm);
          searchForTextElement.innerText = newText;
        }
      }

      getSearchResults(searchTerm) {
        const queryKey = searchTerm.replace(" ", "-").toLowerCase();
        this.setLiveRegionLoadingState();

        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          this.clickSearchTabs();
          return;
        }

        const baseUrl = routes.predictive_search_url;
        const qParam = `q=${encodeURIComponent(searchTerm)}`;
        const limitValue = this.getAttribute("data-results-limit") || 4;
        const limitParam = `resources[limit]=${limitValue}`;
        const scopeParam = `resources[limit_scope]=each`;
        const requestUrl = `${baseUrl}?${qParam}&${limitParam}&${scopeParam}&section_id=predictive-search`;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              this.close();
              throw error;
            }
            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector("#shopify-section-predictive-search").innerHTML;
            // Save bandwidth keeping the cache in all instances synced
            this.allPredictInstances.forEach((predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            });
            this.renderSearchResults(resultsMarkup);
            this.clickSearchTabs();
          })
          .catch((error) => {
            if (error?.code === 20) {
              // Code 20 means the call was aborted
              return;
            }
            this.close();
            throw error;
          });
      }

      setLiveRegionLoadingState() {
        this.statusElement =
          this.statusElement || this.querySelector(".predictive-search-status");
        this.loadingText =
          this.loadingText || this.getAttribute("data-loading-text");

        this.setLiveRegionText(this.loadingText);
        this.setAttribute("loading", true);
      }

      setLiveRegionText(statusText) {
        this.statusElement.setAttribute("aria-hidden", "false");
        this.statusElement.textContent = statusText;

        setTimeout(() => {
          this.statusElement.setAttribute("aria-hidden", "true");
        }, 1000);
      }

      renderSearchResults(resultsMarkup) {
        this.predictiveSearchEl.innerHTML = resultsMarkup;
        this.setAttribute("results", true);

        this.setLiveRegionResults();
        this.open();
        this.retrapModalFocus();
      }

      // The header modal's trap is re-armed after a results change, but focus stays
      // wherever the visitor already moved it inside the modal.
      retrapModalFocus() {
        const parentModal = this.closest(".header-search-modal");
        if (!parentModal) return;
        const focusTarget = parentModal.contains(document.activeElement)
          ? document.activeElement
          : this.input;
        trapFocus(parentModal, focusTarget);
      }

      // The merchant sets how many results a type shows; the desktop panel and
      // the narrower modal / in-page dropdown get their own count. The host
      // element is what tells them apart — the panel only exists on desktop.
      get resultsShown() {
        const inPanel = Boolean(this.closest(".header-search-panel"));
        const value = inPanel
          ? this.dataset.resultsShown
          : this.dataset.resultsShownMobile;
        const count = parseInt(value, 10);
        return count > 0 ? count : 3;
      }

      // Everything the batch returned is rendered; the rest of it is held back
      // here, and "Show all results" earns its place only when there is one.
      applyResultsLimit() {
        const limit = this.resultsShown;
        this.querySelectorAll(".predictive-search__list").forEach((list) => {
          Array.from(list.children).forEach((item, index) => {
            item.classList.toggle("hidden", index >= limit);
          });
        });
      }

      syncResultsFooter() {
        const footer = this.querySelector("[data-search-footer]");
        if (!footer) return;

        const active = this.querySelector(".predictive-search__list.active");
        if (!active) {
          footer.classList.add("hidden");
          return;
        }

        footer.classList.toggle(
          "hidden",
          active.children.length <= this.resultsShown
        );
      }

      clickSearchTabs() {
        const tabs = this.querySelectorAll(".predictive-search__tab");
        const results = this.querySelectorAll(".predictive-search__list");
        if (tabs.length === 0 || results.length === 0) return;

        tabs[0].classList.add("active");
        results[0].classList.add("active");
        this.applyResultsLimit();
        this.syncResultsFooter();

        const onClickTab = (event, tab) => {
          event.preventDefault();
          const typeTarget = tab.dataset.typeTarget;

          tabs.forEach((element) => {
            element.classList.remove("active");
          });
          tab.classList.add("active");

          results.forEach((element) => {
            let resultsType = element.dataset.type;
            if (resultsType == typeTarget) {
              element.classList.add("active");
            } else {
              element.classList.remove("active");
            }
          });

          this.syncResultsFooter();
        };

        tabs.forEach((tab) => {
          tab.addEventListener("click", (event) => onClickTab(event, tab));
        });
      }

      setLiveRegionResults() {
        this.removeAttribute("loading");
        this.setLiveRegionText(
          this.querySelector("[data-predictive-search-live-region-count-value]")
            .textContent
        );
      }

      open() {
        this.setAttribute("open", true);
        this.input.setAttribute("aria-expanded", true);
        this.isOpen = true;
      }

      close(clearSearchTerm = false) {
        this.closeResults(clearSearchTerm);
        this.isOpen = false;
        this.retrapModalFocus();
      }

      closeResults(clearSearchTerm = false) {
        if (clearSearchTerm) {
          this.input.value = "";
          this.removeAttribute("results");
          if (this.resetBtn) this.resetBtn.removeAttribute("style");
        }
        const selected = this.querySelector('[aria-selected="true"]');

        if (selected) selected.setAttribute("aria-selected", false);

        this.input.setAttribute("aria-activedescendant", "");
        this.removeAttribute("loading");
        this.removeAttribute("open");
        this.input.setAttribute("aria-expanded", false);
        this.predictiveSearchEl.removeAttribute("style");
      }
    }
  );
}
